// src/pages/OnboardingCsvUpload.jsx
import React, { useState } from "react";
import { Button } from "primereact/button";
import { FileUpload } from "primereact/fileupload";
import Papa from "papaparse";
import axiosInstance from "../services/axiosInstance"; // your axios wrapper
import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { Checkbox } from "primereact/checkbox";

const REQUIRED_COLUMNS = ["name", "code", "organization_id"];

export default function OnboardingCsvUpload () {
  const [file, setFile] = useState(null);
  const [previewRows, setPreviewRows] = useState([]);
  const [errors, setErrors] = useState([]);
  const [dryRun, setDryRun] = useState(false);
  const [uploading, setUploading] = useState(false);
  const toastRef = React.useRef(null);

  const validateColumns = (header) => {
    const lower = header.map((h) => (h || "").toString().trim().toLowerCase());
    const missing = REQUIRED_COLUMNS.filter((col) => !lower.includes(col));
    return missing;
  };

  const onSelect = (e) => {
    const f = e.files && e.files[0];
    setErrors([]);
    setPreviewRows([]);
    setFile(null);

    if (!f) return;

    // client-side accept only .csv
    if (!/\.csv$/i.test(f.name)) {
      setErrors([{ row: 0, error: "Only .csv files are allowed." }]);
      toastRef.current.show({ severity: "error", summary: "Invalid file", detail: "Only CSV allowed", life: 4000 });
      return;
    }

    setFile(f);

    // parse first 50 rows to validate headers and show preview
    Papa.parse(f, {
      header: true,
      preview: 50,
      skipEmptyLines: true,
      complete: (res) => {
        if (res && res.meta && res.meta.fields) {
          const header = res.meta.fields;
          const missing = validateColumns(header);
          if (missing.length > 0) {
            setErrors([{ row: 0, error: "Missing required columns: " + missing.join(", ") }]);
            toastRef.current.show({ severity: "error", summary: "Missing columns", detail: missing.join(", "), life: 6000 });
            return;
          }
          // basic row-level validation (first 10 rows)
          const rows = res.data.slice(0, 10);
          const rowErrors = [];
          rows.forEach((r, idx) => {
            const rowNum = idx + 1;
            REQUIRED_COLUMNS.forEach((col) => {
              const val = r[col] ?? r[col.toUpperCase()] ?? r[col.toLowerCase()];
              if (val === undefined || val === null || ("" + val).trim() === "") {
                rowErrors.push({ row: rowNum, error: `Missing ${col}` });
              }
            });
          });

          setPreviewRows(rows);
          if (rowErrors.length > 0) {
            setErrors(rowErrors);
            toastRef.current.show({ severity: "warn", summary: "Row validation issues", detail: `Found ${rowErrors.length} issues in preview`, life: 6000 });
          } else {
            setErrors([]);
            toastRef.current.show({ severity: "success", summary: "File ready", detail: "CSV validated (preview)", life: 3000 });
          }
        } else {
          setErrors([{ row: 0, error: "Unable to read CSV header" }]);
        }
      },
      error: (err) => {
        setErrors([{ row: 0, error: "CSV parse error: " + err.message }]);
      },
    });
  };

  const onUploadClick = async () => {
    if (!file) {
      toastRef.current.show({ severity: "warn", summary: "No file", detail: "Choose a CSV first", life: 3000 });
      return;
    }

    setUploading(true);
    setErrors([]);

    const form = new FormData();
    form.append("file", file);
    form.append("dry_run", dryRun ? "true" : "false");

    try {
      const resp = await axiosInstance.post("/api/admin/onboarding/upload-items/", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (resp.status === 200 || resp.status === 201) {
        const message = resp.data?.detail || "Import completed";
        toastRef.current.show({ severity: "success", summary: "Success", detail: message, life: 5000 });
        // show server summary if present
        console.info("Server response:", resp.data);
      } else {
        toastRef.current.show({ severity: "info", summary: "Result", detail: JSON.stringify(resp.data), life: 5000 });
      }
    } catch (err) {
      // surface row-level errors if backend returns them
      if (err.response?.data) {
        const serverData = err.response.data;
        if (serverData.summary?.errors) {
          setErrors(serverData.summary.errors);
        } else if (serverData.errors) {
          setErrors(serverData.errors);
        }
        toastRef.current.show({ severity: "error", summary: "Import failed", detail: serverData.detail || err.message, life: 6000 });
      } else {
        toastRef.current.show({ severity: "error", summary: "Network", detail: err.message, life: 6000 });
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="p-grid p-justify-center">
      <Toast ref={toastRef} />
      <div className="p-col-10">
        <Card title="Onboarding CSV upload (gf-admin only)">
          <p style={{ marginTop: 0, color: "#666" }}>
            Required columns: <code>name</code>, <code>code</code>, <code>organization_id</code>.
            Optional columns: <code>quantity</code>, <code>selling_price</code>.
          </p>
          <div className="p-field-checkbox">
            {/* <Checkbox inputId="dryrun" checked={dryRun} onChange={(e) => setDryRun(e.checked)} /> */}
            <label htmlFor="dryrun" style={{ marginLeft: "8px" }}>Split the CSV into maximum 4000 records</label>
          </div>

          <FileUpload
            name="file"
            auto={false}
            chooseLabel="Choose CSV"
            uploadLabel="Upload"
            multiple={false}
            accept=".csv"
            maxFileSize={10 * 1024 * 1024}
            customUpload
            uploadHandler={() => {}}
            onSelect={onSelect}
            onError={(e) => {
              toastRef.current.show({ severity: "error", summary: "File error", detail: e, life: 4000 });
            }}
            emptyTemplate={<p className="p-m-0">Drag and drop a CSV file here to upload</p>}
          />

          <div style={{ marginTop: 16 }}>
            <Button label="Upload to server" icon="pi pi-upload" onClick={onUploadClick} disabled={uploading || !file} loading={uploading} />
          </div>

          <div style={{ marginTop: 20 }}>
            <h4>Preview (first 10 rows)</h4>
            {previewRows.length === 0 ? <p>No preview</p> : (
              <div style={{ maxHeight: 240, overflow: "auto" }}>
                <table className="p-datatable p-component" style={{ width: "100%" }}>
                  <thead>
                    <tr>
                      {Object.keys(previewRows[0]).map((h) => <th key={h}>{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((r, idx) => (
                      <tr key={idx}>
                        {Object.keys(previewRows[0]).map((h) => <td key={h}>{r[h]}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div style={{ marginTop: 16 }}>
            <h4>Validation errors</h4>
            {errors.length === 0 ? <p>No errors</p> : (
              <ul>
                {errors.map((e, i) => <li key={i}><strong>Row {e.row}:</strong> {e.error}</li>)}
              </ul>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};